/**
 * Per-user Inbox hide. Sharetribe cannot delete transactions, so we
 * archive locally and on the app server. The other party still sees the thread.
 */

export const INBOX_ARCHIVE_STORAGE_KEY = 'sl-inbox-hidden-v1';
export const INBOX_ARCHIVE_CHANGE_EVENT = 'sl-inbox-hidden-change';
export const HIDDEN_INBOX_TX_IDS_KEY = 'hiddenInboxTxIds';
export const MAX_HIDDEN_INBOX_TX_IDS = 200;

const emptyUserState = () => ({ hiddenTxIds: [] });

/**
 * Transaction or user id from an SDK entity, UUID, or string.
 *
 * @param {string|{uuid?: string, id?: string|{uuid?: string}}|null|undefined} value
 * @returns {string|null}
 */
export const inboxEntityId = value => {
  if (!value) {
    return null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value.uuid === 'string' && value.uuid) {
    return value.uuid;
  }
  if (typeof value.id === 'string' && value.id) {
    return value.id;
  }
  if (value.id && typeof value.id.uuid === 'string' && value.id.uuid) {
    return value.id.uuid;
  }
  return null;
};

const canUseStorage = () => {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch (e) {
    return false;
  }
};

const readStore = () => {
  if (!canUseStorage()) {
    return { users: {} };
  }
  try {
    const parsed = JSON.parse(window.localStorage.getItem(INBOX_ARCHIVE_STORAGE_KEY));
    if (parsed && typeof parsed === 'object' && parsed.users && typeof parsed.users === 'object') {
      return parsed;
    }
  } catch (e) {
    // Corrupt storage or blocked access — start clean.
  }
  return { users: {} };
};

const writeStore = store => {
  if (!canUseStorage()) {
    return;
  }
  try {
    window.localStorage.setItem(INBOX_ARCHIVE_STORAGE_KEY, JSON.stringify(store));
    window.dispatchEvent(new Event(INBOX_ARCHIVE_CHANGE_EVENT));
  } catch (e) {
    // Quota / private mode — React state still hides the row for this visit.
  }
};

const userBucket = (store, userId) => {
  const existing = store.users[userId];
  if (existing && typeof existing === 'object' && Array.isArray(existing.hiddenTxIds)) {
    return { hiddenTxIds: existing.hiddenTxIds.filter(id => typeof id === 'string') };
  }
  return emptyUserState();
};

/**
 * Hidden transaction ids for this user.
 *
 * @param {string|{uuid?: string}|null|undefined} userId
 * @returns {string[]}
 */
export const hiddenInboxTransactionIds = userId => {
  const uid = inboxEntityId(userId);
  if (!uid) {
    return [];
  }
  return userBucket(readStore(), uid).hiddenTxIds;
};

/**
 * Hide a conversation from this person's Inbox only.
 *
 * @param {string|{uuid?: string}|null|undefined} userId
 * @param {string|{uuid?: string, id?: {uuid?: string}}|null|undefined} transactionId
 */
export const hideInboxTransaction = (userId, transactionId) => {
  const uid = inboxEntityId(userId);
  const txId = inboxEntityId(transactionId);
  if (!uid || !txId) {
    return;
  }
  const store = readStore();
  const current = userBucket(store, uid);
  if (current.hiddenTxIds.includes(txId)) {
    return;
  }
  store.users[uid] = {
    hiddenTxIds: [...current.hiddenTxIds, txId].slice(-MAX_HIDDEN_INBOX_TX_IDS),
  };
  writeStore(store);
};

/**
 * Merge server-hidden ids into local storage.
 *
 * @param {string|{uuid?: string}|null|undefined} userId
 * @param {string[]} transactionIds
 * @returns {string[]}
 */
export const mergeHiddenInboxTransactionIds = (userId, transactionIds = []) => {
  const uid = inboxEntityId(userId);
  const extras = (Array.isArray(transactionIds) ? transactionIds : [])
    .map(inboxEntityId)
    .filter(Boolean);
  if (uid) {
    extras.forEach(txId => hideInboxTransaction(uid, txId));
    return hiddenInboxTransactionIds(uid);
  }
  return extras;
};

/**
 * True when this user archived the thread.
 *
 * @param {string} userId
 * @param {string} transactionId
 * @returns {boolean}
 */
export const isInboxTransactionHidden = (userId, transactionId) => {
  const txId = inboxEntityId(transactionId);
  return !!txId && hiddenInboxTransactionIds(userId).includes(txId);
};

/**
 * Deduped hidden conversation ids, newest kept last.
 *
 * @param {unknown} ids
 * @returns {string[]}
 */
export const normalizeHiddenInboxIds = ids => {
  const list = Array.isArray(ids) ? ids : [];
  return [...new Set(list.map(inboxEntityId).filter(Boolean))].slice(-MAX_HIDDEN_INBOX_TX_IDS);
};

/**
 * Hidden ids from the signed-in user's privateData plus local cache.
 *
 * @param {object|null|undefined} currentUser
 * @returns {string[]}
 */
export const hiddenInboxTransactionIdsFromUser = currentUser => {
  const profileIds = currentUser?.attributes?.profile?.privateData?.[HIDDEN_INBOX_TX_IDS_KEY];
  return normalizeHiddenInboxIds([
    ...normalizeHiddenInboxIds(profileIds),
    ...hiddenInboxTransactionIds(inboxEntityId(currentUser)),
  ]);
};

/**
 * Optimistic currentUser shape after hiding a conversation.
 *
 * @param {object} user
 * @param {string[]} ids
 * @returns {object}
 */
export const applyHiddenInboxIdsToUser = (user, ids) => {
  if (!user?.attributes?.profile) {
    return user;
  }
  return {
    ...user,
    attributes: {
      ...user.attributes,
      profile: {
        ...user.attributes.profile,
        privateData: {
          ...(user.attributes.profile.privateData || {}),
          [HIDDEN_INBOX_TX_IDS_KEY]: normalizeHiddenInboxIds(ids),
        },
      },
    },
  };
};
